/**
 * Fullscreen overlay for a single image.
 *
 * Extracted from the file preview's ImageRenderer (#510) so the Communicator's
 * split-view preview can offer the same escape hatch (#714) instead of growing
 * a second copy of it. Only the overlay is shared — the fitted view stays with
 * each caller, because the two differ in padding, canvas and framing, and
 * parameterising that would buy nothing.
 *
 * Rendered through a portal on ``document.body``: a ``fixed`` overlay is
 * positioned against its nearest transformed ancestor, and inside a webOS
 * window that ancestor is not the viewport. ``z-[15000]`` puts it above the
 * taskbar (``z-[10000]``) — at 9999 the original overlay slid underneath it.
 *
 * Labels come from ``filePreview.*``: the wording is generic ("Vollbild") and
 * already translated in both locales, so a second set of keys for the same two
 * words would only be a chance for them to drift apart.
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Minimize2 } from 'lucide-react'

interface ImageLightboxProps {
  src: string
  alt?: string
  onClose: () => void
  /** Propagated from the fitted view so a broken URL surfaces there too. */
  onError?: () => void
}

export function ImageLightbox({ src, alt, onClose, onError }: ImageLightboxProps) {
  const { t } = useTranslation()
  const closeLabel = t('filePreview.exitFullscreen')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[15000] flex items-center justify-center"
      style={{ backgroundColor: '#1a1a1a' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      {/* The image closes what the image opened. Clicking it again is the
          first thing people try, and the Esc hint lives in a tooltip nobody
          reads — leaving only the corner button was a trap. aria-label rather
          than title: a native tooltip over an image this size hangs around
          after the pointer moves on, and the cursor already says what a click
          does. */}
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="flex max-h-[95vh] max-w-[95vw]"
        style={{ cursor: 'zoom-out' }}
      >
        <img
          src={src}
          alt={alt ?? ''}
          className="max-h-[95vh] max-w-[95vw] object-contain"
          onError={onError}
        />
      </button>
      <button
        type="button"
        onClick={onClose}
        title={closeLabel}
        aria-label={closeLabel}
        className="absolute right-4 top-4 rounded-full p-2 transition-opacity hover:opacity-70"
        style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer' }}
      >
        <Minimize2 className="h-5 w-5" />
      </button>
    </div>,
    document.body,
  )
}
